import { Box, type BoxProps, Column, Row } from "@hightouch/htui";
import {
  createContext,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useContext,
} from "react";

import { Helmet } from "react-helmet";
import {
  PageRailContext,
  ReportPageRailContext,
  usePageRail,
} from "src/components/page-rail";
import { CardScrollArea, Paper } from "src/components/paper";
import {
  CrumbBarEnd,
  CrumbBarEndSlot,
  GeneratedCrumbBarContext,
  MiniNavCrumbTrail,
  useCrumbBar,
  useHasCrumbBars,
} from "src/components/layout/nav/mini-nav/mini-nav-context";
import {
  type SidebarPanel,
  SidebarPanelRow,
} from "src/components/layout/sidebar-panel-row";
import { Breadcrumbs } from "./breadcrumbs";
import { withCurrentPageCrumb } from "./crumbs";
import { PageBanners } from "./header/page-banners";
import {
  PageScrollProvider,
  usePageScroll,
  usePageScrollRegion,
} from "./page-scroll";
import {
  PageCardProvider,
  PageCardTopBar,
  usePageCardValue,
} from "./page-card-top-bar";
import type { Crumb, PageBg } from "./types";
import {
  PAGE_CARD_HEADER_PADDING_Y,
  TITLE_ROW_MIN_HEIGHT,
} from "src/components/layout/page-card-metrics";

export type NewPageProps = PropsWithChildren<{
  /**
   * If true, the page banners (including `outsideTopbar`) are not displayed.
   * @default false
   */
  hideBanners?: boolean;
  /** Browser-tab (Helmet) title — never rendered on the page. */
  title?: string;
  /** Optional sidebar to display in the page */
  sidebar?: ReactNode;
  /** A panel beside the sidebar that slides in over the page card while open. Requires `sidebar`. */
  sidebarPanel?: SidebarPanel;
  /** If true, the page will be constrained to the height of the screen (no body scroll) */
  contain?: boolean;
  /** Optional additional content to display above the page content and below the page banners */
  outsideTopbar?: ReactNode;
  /** If true, renders children directly without the Paper wrapper (full-bleed layout) */
  flush?: boolean;
  /** Breadcrumbs rendered in a `PageCardTopBar` at the top of the page card.
   *  The current page (`title`) is appended when the trail ends in a link.
   *  Beside the product nav, a page without crumbs gets a trail from its place in the nav.
   *  Not rendered in `flush` mode (there is no card). */
  crumbs?: Crumb[];
  /** A crumb bar the shell draws itself, with more than crumbs in it, in place of the one `crumbs` gives. */
  topBar?: ReactNode;
  /** Controls at the right end of the crumb bar NewPage draws, as every page's draws beside the product nav; a `topBar` places its own. */
  crumbBarEnd?: ReactNode;
  /** No crumb bar in either state, for a view whose own header is its wayfinding; `[` and the gutter still reach the sidebar. */
  hideCrumbBar?: boolean;
}>;

export const NewPage: FC<NewPageProps> = ({
  children,
  title,
  sidebar,
  sidebarPanel,
  outsideTopbar,
  crumbs,
  topBar,
  crumbBarEnd,
  hideCrumbBar = false,
  contain = false,
  hideBanners = false,
  flush = false,
}) => {
  const metadataContent = (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );

  const bannerContent = !hideBanners && (
    <PageBanners>{outsideTopbar}</PageBanners>
  );

  const resolvedCrumbs = crumbs?.length
    ? withCurrentPageCrumb(crumbs, title)
    : undefined;
  // Beside the product nav the window never scrolls, so the card holds its height and scrolls its own content.
  const pageScroll = usePageScroll();
  const isWindowScroll = pageScroll.isWindow;
  const cardScroll = usePageScrollRegion("card");
  const isCardScroll = !isWindowScroll && !contain;
  const hasCrumbBars = useHasCrumbBars();
  // A scrolling card with no crumb bar to stick, as in the settings modal.
  const card = usePageCardValue(isCardScroll && !hasCrumbBars);
  // The card's row is the last, and the only one without banners; it never grows past what's left of the window.
  const rows = isWindowScroll
    ? "auto 1fr"
    : hideBanners
      ? "minmax(0, 1fr)"
      : "auto minmax(0, 1fr)";
  const height = isWindowScroll ? undefined : "100%";
  const { isBesideRail, reportRail } = usePageRail(Boolean(sidebar));
  // A trail the page didn't pass folds away while the sidebar is docked, unless the bar holds the page's own controls.
  const isGeneratedBar =
    !topBar && !resolvedCrumbs && card.ownCrumbBarCount === 0;
  const foldsWhenDocked =
    !hideCrumbBar && isGeneratedBar && hasCrumbBars && !crumbBarEnd;

  const crumbsBar = hideCrumbBar ? (
    <CrumbBarStandIn />
  ) : (
    (topBar ??
    (resolvedCrumbs ? (
      <PageCrumbBar
        crumbs={resolvedCrumbs}
        isContained={contain}
        end={crumbBarEnd}
      />
    ) : card.ownCrumbBarCount > 0 ? null : (
      <PageCrumbBar
        crumbs={[]}
        isGenerated
        foldsWhenDocked={foldsWhenDocked}
        isContained={contain}
        end={crumbBarEnd}
      />
    )))
  );

  const cardContent = flush ? (
    <CardScrollArea
      scrollRef={isCardScroll ? cardScroll.ref : undefined}
      // Clipped, never scrollable: only a contained page's own panes scroll, so nothing can move the card.
      {...(contain && { overflow: "clip", overscrollBehavior: "contain" })}
    >
      {children}
    </CardScrollArea>
  ) : (
    <Paper
      // Clipped, never scrollable: a focus or a scroll into view inside a contained card can't move it.
      overflow={contain ? "clip" : undefined}
      overscrollBehavior={contain ? "contain" : undefined}
      // If we have a sidebar, the content will be inside a row.
      // Setting the flex to 1 will make the content take up the remaining space.
      flex={sidebar ? 1 : undefined}
      minHeight={contain || sidebar || !isWindowScroll ? 0 : undefined}
      isInset={!isWindowScroll}
      scrollRef={isCardScroll ? cardScroll.ref : undefined}
      topBar={crumbsBar}
      // For the card's own sticky parts, which sit below its sticky crumb bar.
      sx={{ "--page-card-sticky-top": `${card.stickyTop}px` }}
    >
      {children}
    </Paper>
  );

  const pageContent = (
    <PageRailContext.Provider value={isBesideRail}>
      {/* Only the rail beside the card reports to it, never one inside the card. */}
      <ReportPageRailContext.Provider value={null}>
        <PageCardProvider value={card}>
          <TitleRowEndContext.Provider value={foldsWhenDocked}>
            <PageScrollProvider
              value={isCardScroll ? cardScroll.value : pageScroll}
            >
              {cardContent}
            </PageScrollProvider>
          </TitleRowEndContext.Provider>
        </PageCardProvider>
      </ReportPageRailContext.Provider>
    </PageRailContext.Provider>
  );

  const railContent = (
    <ReportPageRailContext.Provider value={reportRail}>
      {sidebar}
    </ReportPageRailContext.Provider>
  );

  if (sidebar && sidebarPanel) {
    return (
      <Box display="grid" gridTemplateRows={rows} h={height}>
        {metadataContent}
        {bannerContent}
        <SidebarPanelRow
          panel={sidebarPanel}
          sidebar={railContent}
          isWindowScroll={isWindowScroll}
        >
          {pageContent}
        </SidebarPanelRow>
      </Box>
    );
  }

  if (sidebar) {
    return (
      <Box display="grid" gridTemplateRows={rows} h={height}>
        {metadataContent}
        {bannerContent}
        <Row flex={1} minWidth={0} minHeight={isWindowScroll ? undefined : 0}>
          {pageContent}
          {railContent}
        </Row>
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateRows={rows}
      h="100%"
      maxH={contain ? "100vh" : "100%"}
      overflow={contain ? "clip" : undefined}
    >
      {metadataContent}
      {bannerContent}
      {pageContent}
    </Box>
  );
};

/** Holds a hidden crumb bar's place, so the collapsed sidebar's toggle doesn't float over the view's own header instead. */
const CrumbBarStandIn = () => {
  useCrumbBar();
  return null;
};

/** The card's crumb bar, its first row: beside the product nav it sticks, and a page with no crumbs gets a trail from its nav place. */
const PageCrumbBar: FC<{
  crumbs: Crumb[];
  /** The bar drawn for a page with no crumbs, which shows only beside the product nav. */
  isGenerated?: boolean;
  /** Folds away while the docked sidebar already says where the page is. */
  foldsWhenDocked?: boolean;
  /** The card holds the window's height and scrolls its panes, so the bar stays put without sticking. */
  isContained: boolean;
  end?: ReactNode;
}> = ({
  crumbs,
  isGenerated = false,
  foldsWhenDocked = false,
  isContained,
  end,
}) => {
  const isProductBar = useCrumbBar();
  // Read here, not in the page, so a fold re-renders the bar and not the page it heads.
  const isGeneratedBarShown = useContext(GeneratedCrumbBarContext);
  const isFolded = foldsWhenDocked && !isGeneratedBarShown;
  if (!isProductBar && isGenerated) return null;
  return (
    <PageCardTopBar
      isSticky={isProductBar && !isContained}
      isFolded={isFolded}
      data-nav-toggle-row={isProductBar || undefined}
      // Its right end as deep as its left, for the agent panel's toggle that mirrors the sidebar's.
      {...(isProductBar && { pr: 6 })}
    >
      <CrumbBarTrail crumbs={crumbs} isProductBar={isProductBar} />
      {(isProductBar || end) && (
        <CrumbBarEnd hasSlot={isProductBar && !isFolded}>{end}</CrumbBarEnd>
      )}
    </PageCardTopBar>
  );
};

/** A crumb bar's trail: beside the product nav, its toggle while the sidebar is collapsed, and the trail under the page's nav place. */
export const CrumbBarTrail: FC<{
  crumbs: Crumb[];
  isProductBar: boolean;
}> = ({ crumbs, isProductBar }) =>
  isProductBar ? (
    <MiniNavCrumbTrail crumbs={crumbs} />
  ) : (
    <Breadcrumbs showIcon crumbs={crumbs} />
  );

type PageCardHeaderProps = {
  flush?: boolean;
  removeBoxShadow?: boolean;
  hasBorder?: boolean;
} & BoxProps;

/** True where the page's crumb bar folds away while docked, so its title row then holds the agent panel's toggle at its end. */
const TitleRowEndContext = createContext(false);

export const PageCardHeader: FC<PropsWithChildren<PageCardHeaderProps>> = ({
  children,
  flush,
  removeBoxShadow = false,
  hasBorder = false,
  ...props
}) => {
  const foldsWhenDocked = useContext(TitleRowEndContext);
  const isGeneratedBarShown = useContext(GeneratedCrumbBarContext);
  const hostsCrumbBarEnd = foldsWhenDocked && !isGeneratedBarShown;
  const borderProps = hasBorder
    ? ({
        borderBottomWidth: "hairline",
        borderBottomStyle: "solid",
        borderColor: "base.divider",
      } as const)
    : {};

  return (
    <Row
      px={flush ? 0 : 6}
      py={flush ? 0 : PAGE_CARD_HEADER_PADDING_Y}
      gap={4}
      borderTopRadius="2xl"
      zIndex={1}
      boxShadow={
        removeBoxShadow
          ? undefined
          : "0 3px 6px -2px rgba(0, 0, 0, 0.02), 0 1px 1px 0px rgba(0, 0, 0, 0.02), 0 4px 20px 0px #FFF"
      }
      overflow="hidden"
      {...borderProps}
      {...props}
    >
      {children}
      {hostsCrumbBarEnd && (
        // Level with the title's 32px row, however many rows the header holds below it.
        <CrumbBarEndSlot
          placement="titleRow"
          ml="auto"
          alignSelf="flex-start"
          minHeight={TITLE_ROW_MIN_HEIGHT}
          flexShrink={0}
        />
      )}
    </Row>
  );
};

type PageCardContentProps = {
  hasGradient?: boolean;
  flush?: boolean;
  variant?: PageBg;
} & Omit<BoxProps, "bg">;

const bgGradient = "linear(180deg, white, gray.50)";

export const PageCardContent: FC<PropsWithChildren<PageCardContentProps>> = ({
  hasGradient = false,
  children,
  flush,
  variant = "light",
  ...props
}) => {
  return (
    <Column
      flex={1}
      minHeight={0}
      p={flush ? 0 : 6}
      bg={variant === "dark" ? "base.lightBackground" : "white"}
      bgGradient={hasGradient ? bgGradient : undefined}
      {...props}
    >
      {children}
    </Column>
  );
};
